import type { PortalCustomization } from "@redotech/customer-portal-sdk/rpc/schema/portal-customization/get-portal-customization";
import {
  buildButtonColorVarDeclarations,
  buildButtonStyleRules,
  buildTertiaryColorRules,
} from "./build-button-css-rules";
import { buildCheckboxCssRules } from "./build-checkbox-css-rules";
import { buildInputFieldRules } from "./build-input-field-css-rules";

export function buildPortalCustomizationCss(
  customization: PortalCustomization,
  { scope = "" }: { scope?: string } = {},
): string {
  // `s`: descendant-selector prefix (empty in global mode). `root`: selector
  // for setting vars ON the scope element itself (`:root` globally).
  const s = scope ? `${scope} ` : "";
  const root = scope || ":root";

  const {
    accentColor,
    headingFont,
    bodyFont,
    primaryText,
    secondaryText,
    primaryButton,
    secondaryButton,
    tertiaryButton,
    backToStoreText,
  } = deconstructCustomization(customization);

  // 1. CSS variables set on the scope root.
  const cssVarDeclarations: string[] = [];
  const writeCssVars = (
    sourceValue: string | null | undefined,
    targets: readonly string[],
  ) => {
    if (!sourceValue) return;
    for (const target of targets) {
      cssVarDeclarations.push(`${target}: ${sourceValue};`);
    }
  };

  writeCssVars(accentColor, [
    "--redo-primary-color",
    "--redo-primary-button-color",
  ]);
  writeCssVars(headingFont, ["--return-app-title-font-family"]);
  writeCssVars(bodyFont, ["--return-app-body-font-family"]);

  writeCssVars(primaryText, ["--redo-colors-text-text-primary-900"]);
  // The builder exposes only two text-color knobs; `secondary` maps to all
  // four non-primary tiers so merchants aren't managing five colors.
  writeCssVars(secondaryText, [
    "--redo-colors-text-text-secondary-700",
    "--redo-colors-text-text-tertiary-600",
    "--redo-colors-text-text-quaternary-500",
    "--redo-colors-text-text-quinary-400",
  ]);

  cssVarDeclarations.push(
    ...buildButtonColorVarDeclarations("primary", primaryButton?.colors),
    ...buildButtonColorVarDeclarations("secondary", secondaryButton?.colors),
  );

  const rules: string[] = [];
  if (cssVarDeclarations.length > 0) {
    rules.push(`${root} { ${cssVarDeclarations.join(" ")} }`);
  }

  // 2. Selector-based rules.
  if (bodyFont) {
    rules.push(`${root} { font-family: ${bodyFont}; }`);
  }
  if (primaryText) {
    // Target `body` (not `:root`) in global mode so the rule beats the
    // user-agent `body { color: canvastext }` default.
    rules.push(`${scope || "body"} { color: ${primaryText}; }`);
  }
  if (headingFont) {
    rules.push(
      `${s}h1, ${s}h2, ${s}h3, ${s}[data-ui="header"] { font-family: ${headingFont}; }`,
    );
  }
  if (accentColor) {
    // `colors.primary` is the link/accent color — distinct from primary button
    // colors, which come from `buttons.primary.colors`.
    rules.push(`${s}a { color: ${accentColor}; }`);
  }

  // 3. Button style rules (radius, border, padding, font) per hierarchy.
  rules.push(
    ...buildButtonStyleRules(s, "primary", primaryButton),
    ...buildButtonStyleRules(s, "secondary", secondaryButton),
    ...buildButtonStyleRules(s, "tertiary", tertiaryButton),
  );

  // 4. Tertiary button colors. Tertiary reuses secondary's fg token in the
  // design system, so apply its colors as direct selector rules instead of
  // CSS vars.
  rules.push(...buildTertiaryColorRules(s, tertiaryButton?.colors));

  // 5. Input field rules.
  rules.push(...buildInputFieldRules(s, customization.inputFields));

  // 6. Checkbox color rules (selection cards).
  rules.push(...buildCheckboxCssRules(s, customization.style?.colors));

  // 7. Login page — back-to-store link.
  if (backToStoreText) {
    // `!important` beats the inline `style={{ color }}` on the login.tsx link.
    rules.push(
      `${s}[data-ui="back-to-store"] { color: ${backToStoreText} !important; }`,
    );
  }

  return rules.join("\n");
}

function deconstructCustomization(customization: PortalCustomization) {
  return {
    accentColor: customization.colors?.primary ?? undefined,
    headingFont: customization.typography?.fonts?.heading ?? undefined,
    bodyFont: customization.typography?.fonts?.body ?? undefined,
    primaryText: customization.typography?.textColors?.primary ?? undefined,
    secondaryText: customization.typography?.textColors?.secondary ?? undefined,
    primaryButton: customization.buttons?.primary,
    secondaryButton: customization.buttons?.secondary,
    tertiaryButton: customization.buttons?.tertiary,
    backToStoreText:
      customization.pages?.login?.colors?.backToStoreText ?? undefined,
  };
}
